import { useEffect, useRef } from "react";
import { circadiaEngineProvider } from "../AppController";
import { useCircadiaBridgeReady } from "./useCircadiaBridgeReady";
import { useAppDispatch, useAppSelector } from "./redux";
import {
  setAssignedDevice,
  setDevicePollingTimedOut,
  setSseDeviceOnlineReceived,
  markDeviceOnlineLocally,
  stopDeviceOnlinePolling,
} from "../redux/Clinic/clinic.slice";

/**
 * Hook that subscribes to the `device-online-sse` Lambda for the entire
 * duration of an in-progress provisioning. When the device first hits AWS
 * IoT, the SSE stream delivers a `device_online` event within milliseconds —
 * we flip Redux state to "online" and stop the cloud-poll backstop.
 *
 * Real-time, not poll-reliant
 * ---------------------------
 * The SSE push is the primary signal. When ConnectDevice dispatches
 * `startDeviceOnlinePolling` after a successful provision, this hook opens
 * the stream and waits for a real-time `device_online` push:
 *
 *   - On `device_online`         — flip Redux to online, stop everything.
 *   - After DEVICE_ONLINE_SSE_TIMEOUT_MS with no event — declare "device not
 *     connected" (`setDevicePollingTimedOut(true)`) and stop. We do NOT wait
 *     on the slower cloud poll for this answer.
 *
 * The legacy `useDeviceOnlinePolling` hook still runs in parallel as a
 * belt-and-suspenders backstop; both key off `state.clinic.deviceOnlinePolling`
 * and tear down when it clears.
 *
 * Auth
 * ----
 * The native plugin opens the Lambda Function URL (AuthType: NONE) with the
 * Cognito User Pool ID token in an `Authorization: Bearer` header; the Lambda
 * verifies it against the User Pool JWKS. No SigV4 / Identity Pool. Web-only
 * callers silently skip (the native plugin isn't present) — the cloud poll
 * alone handles them.
 */

// ── Configuration ─────────────────────────────────────────────────────────
//
// Override via Vite env vars if you want different URLs per build. Default is
// the prod Function URL — flip it in your `.env` (or build target) for dev.
//
// VITE_DEVICE_ONLINE_SSE_URL    — Function URL of the SSE Lambda
//
// Auth no longer needs the Identity Pool / region here: the native plugin
// sends the Cognito User Pool ID token (from `cognitoTokens()`) as a Bearer
// header and the Lambda verifies it. So the only config this hook needs is
// the URL.
const env = (import.meta as { env?: Record<string, string | undefined> }).env ??
  {};

const SSE_URL =
  env.VITE_DEVICE_ONLINE_SSE_URL ??
  "https://felaohoq2pjuz7hlni4kffzbt40fzhfg.lambda-url.us-east-1.on.aws/";

// How long to wait for a real-time `device_online` push before declaring the
// device "not connected". Per product: ~30 s for the common case (a device
// reconnecting). A freshly-provisioned factory device runs a one-time OTA
// first and legitimately needs longer, so when ConnectDevice provides a
// firmware-aware `timeoutMs` on the polling state we honour that instead.
const DEVICE_ONLINE_SSE_TIMEOUT_MS = 30_000;

// ── Type definitions for the native plugin surface ───────────────────────
// We can't import these from `circadiaEngineProvider` directly without
// touching its driven-port file, so we cast `as unknown as { … }` the same
// way ConnectDevice does for other native methods.
interface CircadiaSseSurface {
  // The iOS native plugin's `window.circadiaapp.cognitoTokens` shim returns
  // PascalCase keys (`AccessToken` / `IdToken`) — the Swift function
  // resolves with the camelCase shape, but the injected JS bridge remaps
  // them. Accept both so this hook works regardless of which path runs.
  cognitoTokens?: () => Promise<{
    IdToken?: string;
    AccessToken?: string;
    idToken?: string;
    accessToken?: string;
  }>;
  openDeviceOnlineStream?: (opts: {
    sseUrl: string;
    deviceId: string;
    // Optional fallback only — the native plugin sources the current ID
    // token from the live session itself (refreshing if expired).
    idToken?: string;
  }) => Promise<{ accepted: boolean }>;
  closeDeviceOnlineStream?: () => Promise<{ closed: boolean }>;
  setDeviceOnlineEventCb?: (
    cb: ((data: { event_type?: string; data: string }) => void) | null
  ) => void;
  setDeviceOnlineStreamErrorCb?: (cb: ((reason: string) => void) | null) => void;
  setDeviceOnlineStreamClosedCb?: (cb: ((reason?: string) => void) | null) => void;
}

function normaliseDeviceId(id: string): string {
  return id.replace(/-/g, "").toLowerCase();
}

export function useDeviceOnlineSseStream(): void {
  const dispatch = useAppDispatch();
  const polling = useAppSelector((s) => s.clinic.deviceOnlinePolling);
  const assignedDevice = useAppSelector((s) => s.clinic.assignedDevice);
  // Re-run when the native bridge lands: on Android it's injected after this
  // hook first mounts, so gating solely on the initial value would permanently
  // skip the stream for a provisioning that started before the bridge arrived.
  const bridgeReady = useCircadiaBridgeReady();

  // Refs so the long-lived native callbacks always read the latest values.
  const dispatchRef = useRef(dispatch);
  const assignedDeviceRef = useRef(assignedDevice);
  dispatchRef.current = dispatch;
  assignedDeviceRef.current = assignedDevice;

  useEffect(() => {
    if (!polling) return;

    // Native-only path. Web/Electron silently skip.
    if (!bridgeReady || !circadiaEngineProvider.isWindowCircadia()) return;

    const engine = circadiaEngineProvider as unknown as CircadiaSseSurface;
    if (typeof engine.openDeviceOnlineStream !== "function") return;
    if (typeof engine.cognitoTokens !== "function") return;

    if (!SSE_URL) {
      console.warn(
        "[useDeviceOnlineSseStream] missing VITE_DEVICE_ONLINE_SSE_URL; skipping SSE"
      );
      return;
    }

    const targetDeviceId = normaliseDeviceId(polling.deviceId);
    let cancelled = false;

    // ── "Device not connected" timeout ──────────────────────────────────
    // If no real-time device_online push arrives within the window, declare
    // the device not connected and stop — without waiting on the slower
    // cloud poll. Honour a firmware-aware timeout from the polling state
    // (factory devices OTA first and need longer) else default to 30 s.
    const timeoutMs = polling.timeoutMs ?? DEVICE_ONLINE_SSE_TIMEOUT_MS;
    let notConnectedTimer: ReturnType<typeof setTimeout> | null = null;
    const clearNotConnectedTimer = (): void => {
      if (notConnectedTimer !== null) {
        clearTimeout(notConnectedTimer);
        notConnectedTimer = null;
      }
    };
    notConnectedTimer = setTimeout(() => {
      if (cancelled) return;
      console.log(
        `[useDeviceOnlineSseStream] no device_online within ${timeoutMs}ms ` +
          `for device_id=${targetDeviceId} → device not connected`
      );
      // Surface the "not connected" banner and tear everything down. Stopping
      // the poll clears `deviceOnlinePolling`, which triggers this effect's
      // cleanup (closing the stream + listeners).
      dispatchRef.current(setDevicePollingTimedOut(true));
      dispatchRef.current(stopDeviceOnlinePolling());
      engine.closeDeviceOnlineStream?.();
    }, timeoutMs);

    // ── Wire up listeners BEFORE opening the stream ──────────────────────
    // The native side emits `deviceOnlineEvent` for every parsed SSE record.
    // We filter on the device_id (defence in depth — the Lambda only
    // subscribes to OUR device, but a stray push shouldn't crash us).
    engine.setDeviceOnlineEventCb?.((ev) => {
      if (cancelled) return;
      let payload: { type?: string; device_id?: string };
      try {
        payload = JSON.parse(ev.data);
      } catch {
        // Heartbeats / `ready` / `timeout` events also flow through here.
        // `ready` and `timeout` carry JSON; only malformed events get here.
        return;
      }

      // Only act on the real "device is online" event. Skip `ready` /
      // `timeout` — they're informational.
      if (payload?.type !== "device_online") return;

      const pushDeviceId = normaliseDeviceId(String(payload?.device_id ?? ""));
      if (pushDeviceId !== targetDeviceId) return;

      // Match — got the real-time push. Cancel the not-connected timer,
      // flip Redux device-online state, and stop the cloud poll.
      clearNotConnectedTimer();
      // Always raise the dedicated SSE flag — Step 3 Verify subscribes to
      // this regardless of whether assignedDevice is populated yet (device
      // can come online before the tech presses "Finish Setup").
      dispatchRef.current(setSseDeviceOnlineReceived(true));
      // Record this device as recently-confirmed-online so list screens that
      // read a slower REST status (e.g. Recent setups) can show it online
      // while its first-boot firmware OTA keeps the REST endpoint lagging.
      dispatchRef.current(markDeviceOnlineLocally({ deviceId: pushDeviceId }));
      const device = assignedDeviceRef.current;
      if (device) {
        dispatchRef.current(
          setAssignedDevice({ ...device, device_conn_status: 1 })
        );
      }
      dispatchRef.current(stopDeviceOnlinePolling());

      // Tear down the stream — we got what we needed. The effect's cleanup
      // function would do this too, but doing it here exits the Lambda
      // invocation immediately rather than waiting for Redux to propagate.
      engine.closeDeviceOnlineStream?.();
    });

    engine.setDeviceOnlineStreamErrorCb?.((reason) => {
      // Non-fatal — the cloud poll continues. Log for diagnostics only.
      console.warn("[useDeviceOnlineSseStream] stream error:", reason);
    });

    engine.setDeviceOnlineStreamClosedCb?.((reason) => {
      console.log("[useDeviceOnlineSseStream] stream closed:", reason);
    });

    // ── Open the stream ──────────────────────────────────────────────────
    (async () => {
      try {
        // Best-effort fallback token. The native plugin authoritatively
        // sources the *current* ID token from the live session (refreshing
        // if expired), so we do NOT bail when this is empty — we just hand
        // over whatever we have (if anything) and let native send the real
        // one. A transient cognitoTokens() failure must not block the stream.
        let idToken = "";
        try {
          const tokens = await engine.cognitoTokens?.();
          // PascalCase comes from the iOS JS-bridge shim; camelCase comes
          // from a direct Capacitor call. Read both — whichever is set wins.
          idToken = tokens?.IdToken ?? tokens?.idToken ?? "";
        } catch (tokenErr) {
          console.warn(
            "[useDeviceOnlineSseStream] cognitoTokens() failed; native will source the token:",
            tokenErr
          );
        }
        if (cancelled) return;
        await engine.openDeviceOnlineStream?.({
          sseUrl: SSE_URL,
          deviceId: targetDeviceId,
          ...(idToken ? { idToken } : {}),
        });
        console.log(
          `[useDeviceOnlineSseStream] opened for device_id=${targetDeviceId} url=${SSE_URL}`
        );
      } catch (err) {
        console.warn(
          "[useDeviceOnlineSseStream] failed to open stream (non-fatal):",
          err
        );
      }
    })();

    // ── Cleanup ──────────────────────────────────────────────────────────
    // Runs when the provisioning ends (polling state cleared), when the
    // user navigates away from any screen mounting this hook, or when the
    // SSE event we wanted has been processed (we already called close()
    // there — calling it again is harmless).
    return () => {
      cancelled = true;
      clearNotConnectedTimer();
      engine.setDeviceOnlineEventCb?.(null);
      engine.setDeviceOnlineStreamErrorCb?.(null);
      engine.setDeviceOnlineStreamClosedCb?.(null);
      engine.closeDeviceOnlineStream?.();
    };
  }, [polling, bridgeReady]);
}
